/**
 * Fordonsflottan: filter + sökning i sessionStorage per användar-id.
 * Rensas vid ny inloggning (se authStore) så läget "börjar om" efter login.
 */

export const FLEET_OVERVIEW_SESSION_PREFIX = 'dqfleet:fleetOverview:v1:';

export function fleetOverviewSessionKey(userId: string): string {
  return `${FLEET_OVERVIEW_SESSION_PREFIX}${userId}`;
}

/** Rensa alla sparade fordonsflotta-sessioner (anropas vid login / impersonation). */
export function clearAllFleetOverviewSessionState(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(FLEET_OVERVIEW_SESSION_PREFIX)) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export interface FleetOverviewSessionPayload {
  v: 1;
  search: string;
  searchInput: string;
  organizationFilter: string;
  statusFilter: string;
  connectedFilter: string;
  presetFilter: string;
  usageStatusFilter: string;
  carInfoFilterIds: number[] | null;
  listPage: number;
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
  /** Per-kolumn filter (server-side): nyckel = kolumnens filterKey, värde = fritext. */
  columnFilters: Record<string, string>;
  /** Om filterraden (per-kolumn) är påslagen. */
  columnFiltersEnabled: boolean;
}

/** Plocka ut endast strängvärden ur ett okänt objekt (för säker hydrering av columnFilters). */
function sanitizeColumnFilters(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim() !== '') out[k] = v;
  }
  return out;
}

export function parseFleetOverviewSession(raw: string | null): FleetOverviewSessionPayload | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<FleetOverviewSessionPayload>;
    if (p?.v !== 1) return null;
    return {
      v: 1,
      search: typeof p.search === 'string' ? p.search : '',
      searchInput: typeof p.searchInput === 'string' ? p.searchInput : typeof p.search === 'string' ? p.search : '',
      organizationFilter: typeof p.organizationFilter === 'string' ? p.organizationFilter : '',
      statusFilter: typeof p.statusFilter === 'string' ? p.statusFilter : 'ACTIVE',
      connectedFilter: typeof p.connectedFilter === 'string' ? p.connectedFilter : '',
      presetFilter: typeof p.presetFilter === 'string' ? p.presetFilter : '',
      usageStatusFilter: typeof p.usageStatusFilter === 'string' ? p.usageStatusFilter : '',
      carInfoFilterIds: Array.isArray(p.carInfoFilterIds)
        ? p.carInfoFilterIds.map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : null,
      listPage: Number.isFinite(p.listPage as number) && (p.listPage as number) >= 1 ? Math.floor(p.listPage as number) : 1,
      sortBy: typeof p.sortBy === 'string' && p.sortBy.length > 0 ? p.sortBy : null,
      sortDir: p.sortDir === 'desc' ? 'desc' : 'asc',
      columnFilters: sanitizeColumnFilters(p.columnFilters),
      columnFiltersEnabled: p.columnFiltersEnabled === true,
    };
  } catch {
    return null;
  }
}
